import axiosInstance from '../../services/AxiosInstance';
import type { BadgeCardDesign, BadgeCardLayout } from './badgeCardPdf';
import { resolveUserProfileBaseUrl } from './userProfileQr';

/**
 * Yaka karti QR degeri.
 *
 * ── Neden profil adresi degil de badge token adresi ──────────────────────
 * Badge PDF'i onceden kullanicinin PROFIL adresini QR'a basiyordu
 * (`/user/:userId`). O adres kimligi tasiyor ama KARTI tasimiyordu: turnike
 * onu okudugunda hangi kartin okundugunu, kartin hala gecerli olup olmadigini
 * bilemezdi. Kayip kart pasife alindiginda bile eski PDF calismaya devam
 * ederdi.
 *
 * Artik QR'a kartin otoriter token adresi basiliyor:
 *     https://app.b2meet.com/badge/K1VIP7K9X2P4Q8A
 */

/**
 * "TR / EN" biciminde cift dilli etiket kurar.
 *
 * Fuar uluslararasi: karti eline alan gorevli de, QR'i okutan yabanci
 * ziyaretci de tipi anlayabilmeli. Bu yuzden etiket arayuz dilinden BAGIMSIZ
 * olarak her iki dili de tasir.
 *
 * ⚠️ Iki dil AYNIYSA (ornegin "VIP") tek kez yazilir — "VIP / VIP" anlamsiz
 * olurdu.
 */
export const formatBilingual = (tr?: string | null, en?: string | null): string => {
    const trimmedTr = String(tr || '').trim();
    const trimmedEn = String(en || '').trim();

    if (!trimmedTr) return trimmedEn;
    if (!trimmedEn || trimmedTr === trimmedEn) return trimmedTr;

    return `${trimmedTr} / ${trimmedEn}`;
};

/** Sunucunun her kart icin dondugu tasarim (web_admin → Yaka Karti Ayarlari). */
export interface BadgeDesignView {
    id: string;
    name: string;
    /** API'ye GORECELI yol; `null` ise kart tipine gore hazir gorsel kullanilir. */
    backgroundPath: string | null;
    layout: BadgeCardLayout;
}

/**
 * Sunucunun goreceli tasarim yolunu PDF ureticisinin bekledigi mutlak adrese
 * cevirir — API tabani axios ile ayni kaynaktan gelir.
 */
export const toBadgeCardDesign = (design?: BadgeDesignView | null): BadgeCardDesign | null => {
    if (!design) return null;
    const base = String(axiosInstance.defaults.baseURL || '').replace(/\/+$/, '');
    return {
        backgroundUrl: design.backgroundPath ? `${base}${design.backgroundPath}` : null,
        layout: design.layout,
    };
};

export interface MyBadge {
    qrToken: string;
    badgeUrl: string;
    badgeType: string;
    badgeTypeLabel: string;
    badgeTypeLabelEn: string;
    /*
     * Ad / soyad / firma kart kaydindan gelir. Indirme oncesi sunucu mevcut
     * token'i koruyarak bu alanlari guncel profil/firma bilgisinden tazeler.
     */
    name: string;
    surname: string;
    company: string | null;
    event: { name: string } | null;
    /** Kartin basilacagi tasarim — yoksa tip koduna gore hazir gorsel. */
    design?: BadgeDesignView | null;
}

/** Kullanicinin kendi aktif karti; kart basilmamissa `null`. */
export const getMyBadge = async (eventId?: string, businessId?: string): Promise<MyBadge | null> => {
    try {
        const response = await axiosInstance.get<MyBadge | null>('my-badge', {
            params: { ...(eventId ? { eventId } : {}), ...(businessId ? { businessId } : {}) },
        });
        return response.data ?? null;
    } catch {
        /*
         * Kart bilgisi alinamadiysa PDF akisi tamamen durmamali — cagiran
         * taraf profil QR'ina geri duser (bkz. buildBadgeQrData).
         */
        return null;
    }
};

/**
 * Kart yoksa OLUSTURUR, varsa mevcudu doner (idempotent).
 *
 * "Yaka Kartı" indirme aninda cagriliyor: karti basilmamis kullanici kayit
 * masasinda elle bir islem beklemek zorunda kalmasin.
 *
 * ⚠️ Kart tipi GONDERILMEZ — sunucu kullanicinin etkinlikteki gercek rolunden
 * turetiyor. Istemci tip secebilseydi bir ziyaretci kendine VIP karti
 * bastirabilirdi.
 *
 * Basarisiz olursa (aktif etkinlik yok, katilim onayi yok, ag hatasi) `null`
 * doner; cagiran taraf profil QR'li eski PDF'e geri duser — buton hicbir
 * durumda tamamen calismaz hale gelmemeli.
 */
export const ensureMyBadge = async (eventId?: string, businessId?: string): Promise<MyBadge | null> => {
    try {
        const response = await axiosInstance.post<MyBadge>('my-badge', null, {
            params: { ...(eventId ? { eventId } : {}), ...(businessId ? { businessId } : {}) },
        });
        return response.data ?? null;
    } catch {
        return null;
    }
};

export const ensureBadgeForUser = async (
    userId: string,
    eventId?: string,
    businessId?: string,
): Promise<MyBadge | null> => {
    try {
        const response = await axiosInstance.post<MyBadge>(`my-badge/users/${userId}`, null, {
            params: { ...(eventId ? { eventId } : {}), ...(businessId ? { businessId } : {}) },
        });
        return response.data ?? null;
    } catch {
        return null;
    }
};

const stripTrailingSlash = (value: string) => value.replace(/\/+$/, '');

/**
 * Token'dan public badge adresi kurar.
 *
 * Sunucu zaten `badgeUrl` doner; bu yardimci, sunucunun base URL'i bos
 * dondurdugu durumlar icin istemci tarafinda ayni adresi kurabilmek icin var.
 */
export const buildBadgeQrData = (qrToken: string, baseUrl = resolveUserProfileBaseUrl()) => {
    const token = String(qrToken || '').trim();
    const base = stripTrailingSlash(String(baseUrl || '').trim());

    if (!token || !base) return '';

    return `${base}/badge/${token}`;
};
